import type { components } from '@/api/openapi'

export type ApiErrorEnvelope = components['schemas']['Error']

export function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return typeof v.code === 'string' && typeof v.message === 'string'
}

export type DescribedError = {
  title: string
  message: string
  code: string | undefined
  requestId: string | undefined
  details: Record<string, unknown> | undefined
}

export function describeError(err: unknown): DescribedError {
  if (isApiErrorEnvelope(err)) {
    return {
      title: humanizeCode(err.code),
      message: err.message,
      code: err.code,
      requestId: err.request_id,
      details: err.details,
    }
  }
  if (err instanceof Error) {
    return {
      title: 'Request failed',
      message: err.message,
      code: undefined,
      requestId: undefined,
      details: undefined,
    }
  }
  return {
    title: 'Unknown error',
    message: 'Something went wrong.',
    code: undefined,
    requestId: undefined,
    details: undefined,
  }
}

const CODE_LABELS: Record<string, string> = {
  validation_error: 'Validation failed',
  unauthorized: 'Not authorized',
  not_found: 'Not found',
  conflict: 'Conflict',
  idempotency_mismatch: 'Idempotency mismatch',
  config_invalid: 'Agent config invalid',
  config_fetch_failed: 'Could not fetch harness config',
  vault_missing_secret: 'Missing env var in vault',
  repo_access_denied: 'Repo access denied',
  sandbox_boot_failed: 'Sandbox boot failed',
  template_build_failed: 'Template build failed',
  template_not_ready: 'Template not ready',
  deps_install_failed: 'Dependency install failed',
  setup_hook_failed: 'Setup hook failed',
  agent_execution_failed: 'Agent execution failed',
  agent_timeout: 'Agent timed out',
  commit_push_failed: 'Commit / push failed',
  internal_error: 'Internal error',
}

export function humanizeCode(code: string): string {
  return CODE_LABELS[code] ?? code.replace(/_/g, ' ')
}
